/**
 * Dashboard Header Component
 *
 * Shared fixed header for Virtual Assistant and Virtual Host dashboards
 *
 * Features:
 * - Restaurant logo (with Dine AI fallback)
 * - Restaurant name and location
 * - Multi-location dropdown (if applicable)
 * - Notifications bell
 * - Settings dropdown
 *
 * Author: Dine AI
 * Created: November 21, 2025
 * Updated: December 16, 2025 - Light mode redesign with restaurant branding
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, ChevronDown, MapPin } from 'lucide-react';
import SettingsDropdown from '@/components/dashboard/SettingsDropdown';

interface Location {
  restaurant_id: number;
  city: string;
  state: string;
  menu_items_count?: number;
  items_count?: number;
}

interface DashboardHeaderProps {
  restaurantName: string;
  hasMultipleLocations: boolean;
  currentLocation: Location | null;
  locations: Location[];
  restaurantData: any;
  userEmail: string;
  userName: string;
  isDemoUser: boolean;
  onLocationSwitch?: (location: Location) => void;
  sidebarExpanded: boolean;
  virtualAssistantAvailable?: boolean;
  restaurantLogoUrl?: string | null;
  welcomeName?: string; // First name for "Welcome back, {name}!" greeting
  isStaff?: boolean; // VH Staff users have limited dropdown menu
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  restaurantName,
  hasMultipleLocations,
  sidebarExpanded,
  currentLocation,
  locations,
  restaurantData,
  userEmail,
  userName,
  isDemoUser,
  onLocationSwitch,
  virtualAssistantAvailable = false,
  restaurantLogoUrl,
  welcomeName,
  isStaff = false
}) => {
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const locationButtonRef = useRef<HTMLButtonElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);

  // Click outside handler for location dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        locationButtonRef.current &&
        !locationButtonRef.current.contains(event.target as Node) &&
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLocationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className={`transition-all duration-300 ${sidebarExpanded ? 'ml-72' : 'ml-28'}`}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto px-6 py-2"
        >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Restaurant Logo or Dine AI Logo as fallback */}
            {(restaurantLogoUrl || restaurantData?.restaurant_logo_url) ? (
              <img
                src={restaurantLogoUrl || restaurantData?.restaurant_logo_url}
                alt={`${restaurantName} logo`}
                className="h-20 w-auto max-w-[280px] object-contain"
              />
            ) : (
              <img
                src="/dineai-logo.png"
                alt="Dine AI"
                className="h-12 w-auto"
              />
            )}
            <div className="h-6 w-px bg-gray-200"></div>

            {/* Location (name removed - logo already identifies restaurant) */}
            <div className="flex items-center gap-3">
              {hasMultipleLocations && (
                <div className="relative">
                  <button
                    ref={locationButtonRef}
                    onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg border border-gray-200 hover:bg-gray-200 transition-colors text-sm"
                  >
                    <MapPin className="w-3 h-3 text-blue-600" />
                    <span className="text-gray-700">
                      {currentLocation
                        ? `${currentLocation.city}, ${currentLocation.state}`
                        : restaurantData?.current_location
                          ? `${restaurantData.current_location.city}, ${restaurantData.current_location.state}`
                          : `${restaurantData?.city}, ${restaurantData?.state}`
                      }
                    </span>
                    <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isLocationDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Location Dropdown Menu */}
                  {isLocationDropdownOpen && (
                    <div
                      ref={locationDropdownRef}
                      className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                      <div className="py-1">
                        {locations.map((location: Location) => {
                          const isActive = restaurantData?.rest_id === location.restaurant_id;
                          const itemCount = location.menu_items_count || location.items_count || 0;
                          return (
                            <button
                              key={`location-${location.restaurant_id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onLocationSwitch) {
                                  onLocationSwitch(location);
                                }
                                setIsLocationDropdownOpen(false);
                              }}
                              className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                                isActive ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                <div>
                                  <div className="text-gray-900 text-sm font-medium">
                                    {location.city}, {location.state}
                                  </div>
                                  <div className="text-gray-500 text-xs">
                                    {itemCount} menu items
                                  </div>
                                </div>
                              </div>
                              {isActive && (
                                <span className="text-blue-600 font-medium">✓</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!hasMultipleLocations && restaurantData && (
                <span className="text-gray-500 text-sm">
                  {restaurantData.current_location
                    ? `${restaurantData.current_location.city}, ${restaurantData.current_location.state}`
                    : `${restaurantData.city}, ${restaurantData.state}`
                  }
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Welcome Message */}
            {welcomeName && (
              <span className="text-gray-700 text-sm font-medium hidden md:block">
                Welcome back, <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent font-semibold">{welcomeName}</span>!
              </span>
            )}

            {/* Notifications */}
            <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 h-2 w-2 bg-blue-600 rounded-full" />
            </button>

            {/* Settings Dropdown Component */}
            <SettingsDropdown
              userEmail={userEmail}
              userName={userName}
              isDemoUser={isDemoUser}
              virtualAssistantAvailable={virtualAssistantAvailable}
              lightMode={true}
              isStaff={isStaff}
            />
          </div>
        </div>
      </motion.div>
      </div>
    </div>
  );
};

export default DashboardHeader;
